import { Helmet } from 'react-helmet-async';

/**
 * SEO — bilingual head injector for Cauratech.
 * Props:
 *  - title: string (page title fragment, will be appended to brand)
 *  - description: string
 *  - lang: 'es' | 'en'
 *  - path: string  (canonical path, e.g. '/agenda')
 *  - image: string (absolute URL or path on origin)
 *  - type: 'website' | 'article'
 *  - noindex: boolean (admin pages)
 *  - keywords: string[]
 */
const BRAND = 'CAURATECH';
const SITE_ORIGIN = process.env.REACT_APP_SITE_ORIGIN || 'https://caura.net';
const DEFAULT_IMAGE = '/og-image.svg';

const SEO = ({
  title,
  description,
  lang = 'es',
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  keywords,
}) => {
  const fullTitle = title ? `${title} · ${BRAND}` : `${BRAND} — ${lang === 'es' ? 'Tu identidad secreta tecnológica' : 'Your secret tech identity'}`;
  const desc = description || (lang === 'es'
    ? 'CAURATECH — Confidential Tech Partner. Plataformas a medida, IA aplicada, seguridad gov-grade, blockchain y soluciones bancarias. Desde 2016.'
    : 'CAURATECH — Confidential Tech Partner. Custom platforms, applied AI, gov-grade security, blockchain and banking solutions. Since 2016.');

  // Always use the production domain for canonical/OG (stable across previews + prerender)
  const canonical = `${SITE_ORIGIN}${path}`;
  const absImage = image && image.startsWith('http') ? image : `${SITE_ORIGIN}${image || DEFAULT_IMAGE}`;
  // Bilingual alternates
  const altLang = lang === 'es' ? 'en' : 'es';
  const kw = (keywords && keywords.length
    ? keywords
    : (lang === 'es'
        ? ['cauratech', 'plataformas a medida', 'inteligencia artificial', 'seguridad gov-grade', 'blockchain', 'banca', 'desarrollo de software', 'venezuela']
        : ['cauratech', 'custom platforms', 'applied AI', 'gov-grade security', 'blockchain', 'banking', 'software development', 'venezuela'])
  ).join(', ');

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      <meta name="theme-color" content="#051C2C" />
      <link rel="canonical" href={canonical} />
      {/* hreflang alternates (same path serves both languages via in-app toggle) */}
      <link rel="alternate" hrefLang={lang} href={canonical} />
      <link rel="alternate" hrefLang={altLang} href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={absImage} />
      <meta property="og:locale" content={lang === 'es' ? 'es_ES' : 'en_US'} />
      <meta property="og:locale:alternate" content={lang === 'es' ? 'en_US' : 'es_ES'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={absImage} />
    </Helmet>
  );
};

export default SEO;
